import React, { useContext, useState } from 'react';
import { ConfigurationContext } from '../DataContexts/ConfigurationContext';
import ConfigSection from './ConfigSection';
import { parseIntOrNull } from './configFormat';

const POLICY_MODES = ['CONFIGURED', 'NO_DELETES_PRESET'];

const emptyForm = () => ({
  name: '',
  mode: 'CONFIGURED',
  maxTracksOnDisk: '',
  maxDownloadsPerStartup: '',
  minDownloadsPerStartup: '',
  maxPrioritySubscriptionsOnDisk: '',
});

const formFromPolicy = (policy) => ({
  name: policy.name || '',
  mode: POLICY_MODES.includes(policy.mode) ? policy.mode : 'CONFIGURED',
  maxTracksOnDisk: policy.maxTracksOnDisk ?? '',
  maxDownloadsPerStartup: policy.maxDownloadsPerStartup ?? '',
  minDownloadsPerStartup: policy.minDownloadsPerStartup ?? '',
  maxPrioritySubscriptionsOnDisk: policy.maxPrioritySubscriptionsOnDisk ?? '',
});

const NUMBER_FIELDS = [
  { key: 'maxTracksOnDisk', label: 'Max tracks on disk' },
  { key: 'maxDownloadsPerStartup', label: 'Max downloads per startup' },
  { key: 'minDownloadsPerStartup', label: 'Min downloads per startup' },
  { key: 'maxPrioritySubscriptionsOnDisk', label: 'Max priority subscriptions on disk' },
];

// DJB-165: overview of all machine disk policies with create, edit (backend
// has PUT) and delete.
const DiskPolicySection = () => {
  const { diskPolicies, diskPoliciesLoading, createDiskPolicy, updateDiskPolicy, deleteDiskPolicy } =
    useContext(ConfigurationContext);
  const [editingId, setEditingId] = useState(null); // null = closed, 'new' = create
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setForm(emptyForm());
    setFormError(null);
    setEditingId('new');
  };

  const openEdit = (policy) => {
    setForm(formFromPolicy(policy));
    setFormError(null);
    setEditingId(policy.id);
  };

  const closeForm = () => {
    setEditingId(null);
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError('Give the disk policy a name.');
      return;
    }
    const numbers = {};
    for (const field of NUMBER_FIELDS) {
      const value = parseIntOrNull(form[field.key]);
      if (value == null || value < 0) {
        setFormError(`${field.label} must be a number (0 or more).`);
        return;
      }
      numbers[field.key] = value;
    }
    if (numbers.minDownloadsPerStartup > numbers.maxDownloadsPerStartup) {
      setFormError('Min downloads per startup cannot exceed max downloads per startup.');
      return;
    }
    const payload = { name: form.name.trim(), mode: form.mode, ...numbers };
    setFormError(null);
    setSaving(true);
    try {
      if (editingId === 'new') {
        await createDiskPolicy(payload);
      } else {
        await updateDiskPolicy(editingId, { ...payload, id: editingId });
      }
      closeForm();
    } catch (error) {
      // notification shown by the context; keep the form open for a retry
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (policy) => {
    const confirmed = window.confirm(`Are you sure you want to delete disk policy "${policy.name}"?`);
    if (!confirmed) return;
    try {
      await deleteDiskPolicy(policy.id);
      if (editingId === policy.id) closeForm();
    } catch (error) {
      // notification shown by the context
    }
  };

  return (
    <ConfigSection
      anchorId="section-ondisk-policy"
      title="OnDisk Policy Config"
      actions={
        editingId == null && (
          <button type="button" className="config-primary-btn" onClick={openCreate}>
            New disk policy
          </button>
        )
      }
    >
      <p className="config-section-intro">
        How a machine manages the tracks on its disk: how many it keeps, how many it downloads per
        startup, and how much room priority subscriptions get. A policy is assigned per machine.
      </p>

      {editingId != null && (
        <form className="config-form" onSubmit={handleSubmit}>
          <div className="config-form-row">
            <div className="config-form-field">
              <label htmlFor="disk-policy-name">Name</label>
              <input
                id="disk-policy-name"
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Small disk"
              />
            </div>
            <div className="config-form-field">
              <label htmlFor="disk-policy-mode">Mode</label>
              <select
                id="disk-policy-mode"
                value={form.mode}
                onChange={(e) => setForm((prev) => ({ ...prev, mode: e.target.value }))}
              >
                {POLICY_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="config-form-row">
            {NUMBER_FIELDS.map((field) => (
              <div className="config-form-field" key={field.key}>
                <label htmlFor={`disk-policy-${field.key}`}>{field.label}</label>
                <input
                  id={`disk-policy-${field.key}`}
                  type="number"
                  min="0"
                  step="1"
                  value={form[field.key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          {formError && (
            <p className="config-form-error" role="alert">
              {formError}
            </p>
          )}
          <div className="config-form-actions">
            <button type="button" className="config-ghost-btn" onClick={closeForm}>
              Cancel
            </button>
            <button type="submit" className="config-primary-btn" disabled={saving}>
              {saving ? 'Saving…' : editingId === 'new' ? 'Create disk policy' : 'Save changes'}
            </button>
          </div>
        </form>
      )}

      {diskPoliciesLoading ? (
        <p className="config-muted">Loading disk policies…</p>
      ) : diskPolicies.length === 0 ? (
        <p className="config-muted">No disk policies yet.</p>
      ) : (
        <div className="config-table-scroll">
          <table className="config-overview-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Mode</th>
                <th scope="col">Max tracks on disk</th>
                <th scope="col">Max downloads per startup</th>
                <th scope="col">Min downloads per startup</th>
                <th scope="col">Max priority subscriptions</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {diskPolicies.map((policy) => (
                <tr key={policy.id}>
                  <th scope="row">{policy.name}</th>
                  <td>{policy.mode}</td>
                  <td>{policy.maxTracksOnDisk ?? '—'}</td>
                  <td>{policy.maxDownloadsPerStartup ?? '—'}</td>
                  <td>{policy.minDownloadsPerStartup ?? '—'}</td>
                  <td>{policy.maxPrioritySubscriptionsOnDisk ?? '—'}</td>
                  <td className="config-table-actions">
                    <button
                      type="button"
                      className="config-ghost-btn"
                      onClick={() => openEdit(policy)}
                      aria-label={`Edit disk policy ${policy.name}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="config-danger-btn"
                      onClick={() => handleDelete(policy)}
                      aria-label={`Delete disk policy ${policy.name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ConfigSection>
  );
};

export default DiskPolicySection;
